import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification, NotificationPreferences } from '@cartiq/shared';
import { apiGet, apiPost, apiPut, apiDelete } from './client';
import { queryKeys } from './queryKeys';

export interface NotificationFeed {
  items: Notification[];
  total: number;
  page: number;
  pageSize: number;
  unread: number;
}

export function useNotifications(params: { unreadOnly?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.notifications(params),
    queryFn: () =>
      apiGet<NotificationFeed>('/notifications', {
        params: { pageSize: 20, ...params },
      }),
    // Poll so the bell stays fresh without a websocket on the admin side.
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: queryKeys.notificationsUnread,
    queryFn: () => apiGet<{ unread: number }>('/notifications/unread-count'),
    refetchInterval: 30 * 1000,
    select: (d) => d.unread,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['notifications'] });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/notifications/${id}/read`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost('/notifications/read-all'),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/notifications/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: queryKeys.notificationPrefs,
    queryFn: () => apiGet<NotificationPreferences>('/notifications/preferences'),
    staleTime: 60 * 1000,
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: NotificationPreferences) =>
      apiPut<NotificationPreferences>('/notifications/preferences', prefs),
    onSuccess: (prefs) => qc.setQueryData(queryKeys.notificationPrefs, prefs),
  });
}

// ---- Web Push ----

export interface PushPublicKey {
  publicKey: string | null;
  enabled: boolean;
}

export function usePushPublicKey() {
  return useQuery({
    queryKey: queryKeys.pushPublicKey,
    queryFn: () => apiGet<PushPublicKey>('/notifications/push/public-key'),
    staleTime: 10 * 60 * 1000,
  });
}

export function subscribePush(sub: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}) {
  return apiPost('/notifications/push/subscribe', sub);
}

export function unsubscribePush(endpoint: string) {
  return apiPost('/notifications/push/unsubscribe', { endpoint });
}
